import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Repeat, XCircle, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { http } from "@/lib/api";
import { useApp } from "@/lib/store";

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return null;
  }
};

const PLAN_LABEL = { year: "Annual", month: "Monthly" };

export default function ManageSubscription() {
  const nav = useNavigate();
  const { isPlus, reloadUser } = useApp();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/billing/subscription");
      setSub(data);
    } catch {
      setSub({ active: false });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (kind, path, okMsg) => {
    setBusy(kind);
    try {
      const { data } = await http.post(path);
      setSub(data);
      await reloadUser();
      toast(okMsg);
    } catch {
      toast("Something went wrong. Please try again.");
    } finally {
      setBusy("");
      setConfirmCancel(false);
    }
  };

  const isYear = sub?.interval === "year";
  const renewal = fmtDate(sub?.current_period_end);
  const trialEnd = fmtDate(sub?.trial_end);

  return (
    <Shell>
      <div className="px-6 pt-12 pb-28">
        <button data-testid="ms-back" onClick={() => nav("/settings")}
          className="inline-flex items-center gap-1 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus -ml-1">
          <ArrowLeft size={18} strokeWidth={1.5} /> Settings
        </button>

        <div className="mt-8 flex items-center gap-2">
          <Sparkles size={18} strokeWidth={1.7} className="text-[#C9A227]" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">Your plan</span>
        </div>
        <h1 className="mt-3 font-serif-display text-4xl leading-tight">Manage subscription.</h1>

        {!sub && (
          <p className="mt-8 font-mono-ss text-sm text-[#6B6B6B]" data-testid="ms-loading">Loading your plan…</p>
        )}

        {sub && !sub.active && (
          <div className="mt-8 ss-card p-6" data-testid="ms-inactive">
            <p className="text-[15px] font-medium">No active subscription.</p>
            <p className="mt-2 text-[13.5px] text-[#474747]">
              You're on the free plan — unlimited check-ins, always.
            </p>
            <button data-testid="ms-upgrade" onClick={() => nav("/plus")}
              className="mt-4 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus">
              See Plus
            </button>
          </div>
        )}

        {sub && sub.active && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="mt-8 ss-card p-6" data-testid="ms-current">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B6B6B]">Signal State ID Plus</p>
                  <p className="mt-1 text-[19px] font-serif-display" data-testid="ms-interval">
                    {PLAN_LABEL[sub.interval]} · ${((sub.amount || 0) / 100).toFixed(2)}/{sub.interval === "year" ? "yr" : "mo"}
                  </p>
                </div>
              </div>

              {sub.status === "trialing" && trialEnd && (
                <p className="mt-3 text-[13.5px] text-[#474747]" data-testid="ms-trial">
                  You're in your free trial. Your first payment is on <span className="font-medium">{trialEnd}</span>.
                </p>
              )}

              {sub.cancel_at_period_end ? (
                <p className="mt-3 text-[13.5px] text-[#474747]" data-testid="ms-ending">
                  Plus ends on <span className="font-medium">{renewal}</span>. You'll keep everything until then.
                </p>
              ) : (
                sub.status !== "trialing" && renewal && (
                  <p className="mt-3 text-[13.5px] text-[#474747]" data-testid="ms-renews">
                    Renews on <span className="font-medium">{renewal}</span>.
                  </p>
                )
              )}
            </div>

            {/* Switch interval */}
            {!sub.cancel_at_period_end && (
              <button
                data-testid="ms-switch"
                disabled={!!busy}
                onClick={() => act("switch", "/billing/switch",
                  isYear ? "Switched to monthly — takes effect at your next renewal."
                         : "Switched to annual — takes effect at your next renewal.")}
                className="mt-4 w-full ss-card p-5 flex items-center gap-3 text-left ss-tap ss-focus disabled:opacity-60">
                <Repeat size={18} strokeWidth={1.6} className="text-[#1A1A1A] shrink-0" />
                <div>
                  <p className="text-[14.5px] font-medium">
                    {busy === "switch" ? "Switching…" : isYear ? "Switch to monthly" : "Switch to annual (save 30%)"}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-[#6B6B6B]">
                    Takes effect at your next renewal — no charge today.
                  </p>
                </div>
              </button>
            )}

            {/* Resume if scheduled to cancel */}
            {sub.cancel_at_period_end && (
              <button
                data-testid="ms-resume"
                disabled={!!busy}
                onClick={() => act("resume", "/billing/resume", "Welcome back — your subscription will continue.")}
                className="mt-4 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-3.5 font-medium inline-flex items-center justify-center gap-2 ss-tap ss-focus disabled:opacity-60">
                <RotateCcw size={16} strokeWidth={1.6} />
                {busy === "resume" ? "Resuming…" : "Keep my subscription"}
              </button>
            )}

            {/* Cancel */}
            {!sub.cancel_at_period_end && (
              <div className="mt-8">
                {!confirmCancel ? (
                  <button
                    data-testid="ms-cancel"
                    onClick={() => setConfirmCancel(true)}
                    className="w-full inline-flex items-center justify-center gap-2 text-[13.5px] text-[#8a6d10] hover:text-[#6b5408] ss-focus py-2">
                    <XCircle size={15} strokeWidth={1.6} /> Cancel subscription
                  </button>
                ) : (
                  <div className="ss-card p-5" data-testid="ms-cancel-confirm">
                    <p className="text-[14.5px] font-medium">Cancel your subscription?</p>
                    <p className="mt-2 text-[13px] text-[#474747]">
                      You'll keep Plus until <span className="font-medium">{renewal}</span>, then move to the
                      free plan. Your check-ins and history stay — you'll just lose the Pattern Review,
                      Load &amp; Recovery and Drift Radar. You can resume anytime before then.
                    </p>
                    <div className="mt-4 flex gap-3">
                      <button
                        data-testid="ms-cancel-confirm-btn"
                        disabled={!!busy}
                        onClick={() => act("cancel", "/billing/cancel",
                          "Cancelled — you'll keep Plus until the end of your period.")}
                        className="flex-1 border border-[#1A1A1A] rounded-full px-4 py-3 text-[13.5px] font-medium ss-tap ss-focus disabled:opacity-60">
                        {busy === "cancel" ? "Cancelling…" : "Yes, cancel"}
                      </button>
                      <button
                        data-testid="ms-cancel-keep"
                        onClick={() => setConfirmCancel(false)}
                        className="flex-1 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-4 py-3 text-[13.5px] font-medium inline-flex items-center justify-center gap-1.5 ss-tap ss-focus">
                        <Check size={15} strokeWidth={1.8} /> Keep Plus
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </Shell>
  );
}
